import { useState, type CSSProperties, type ReactNode } from "react";
import { BarChart, PieChart, WidgetCard } from "@/shared/components/ds";
import type { ChartData, DashboardParams } from "../models/dashboard";
import { Captions, distribution, distributionTotal, hasDataPoints, hasRings, hasSlices, hasTimeSeries, LaunchDatesRing, ServedSummary, TimeSeriesChart } from "./charts";
import { LegacyTable } from "./LegacyTable";
import type { TileSpec } from "./tiles";
import { useChartData } from "./useChartData";

function hasData(tile: TileSpec, data: ChartData): boolean {
    switch (tile.kind) {
        case "table": return !!data.header?.length && !!data.data?.length;
        case "pie":
        case "bar": return hasSlices(data);
        case "donut": return hasDataPoints(data);
        case "rings": return hasRings(data);
        default: return hasTimeSeries(data);
    }
}

/**
 * One dashboard widget (MPN-2230): the shell, a skeleton while the warehouse answers, then
 * the chart or table. A tile with nothing to show collapses to one line carrying the
 * server's reason when it gave one. Tables own their sort here, so clicking a head asks the
 * server again with it; `sort` from outside (the Notes order switch) overrides that.
 */
export function TileWidget({ tile, params, sort: outerSort, title, infoTip, actions, style, maxHeight, minWidth }: {
    tile: TileSpec;
    params: DashboardParams;
    sort?: string;
    title?: ReactNode;
    infoTip?: string;
    actions?: ReactNode;
    style?: CSSProperties;
    maxHeight?: number | string;
    minWidth?: number | string;
}) {
    const [ownSort, setOwnSort] = useState(tile.sort ?? "");
    const sort = outerSort ?? ownSort;
    const { data: response, isLoading, error } = useChartData(tile.id, params, sort);
    const data = response?.chart_data ?? undefined;

    const reason = error
        ? "Could not load this widget"
        : response && response.success === false
            ? (response.error_message || "Could not load this widget")
            : data?.data_missing_message || undefined;
    const empty = !isLoading && (!!reason || !data || !hasData(tile, data));

    return (
        <WidgetCard title={title ?? tile.title} loading={isLoading} isEmpty={empty} emptyLabel={reason ?? "No data for this launch"}
            infoTip={infoTip} actions={actions} style={style}
            meta={data && (tile.kind === "pie" || tile.kind === "bar") && !empty ? distributionTotal(data, tile) : undefined}>
            {data && !empty && (
                <>
                    {tile.kind === "table" && <LegacyTable header={data.header} data={data.data} tableStyles={data.table_styles} sort={sort} onSort={setOwnSort} maxHeight={maxHeight} minWidth={minWidth} />}
                    {(tile.kind === "area" || tile.kind === "line") && <TimeSeriesChart data={data} tile={tile} />}
                    {tile.kind === "pie" && <><PieChart series={distribution(data, tile)} /><Captions data={data} /></>}
                    {tile.kind === "bar" && <BarChart series={distribution(data, tile)} />}
                    {tile.kind === "donut" && <LaunchDatesRing data={data} />}
                    {tile.kind === "rings" && <ServedSummary data={data} />}
                </>
            )}
        </WidgetCard>
    );
}
